import { css } from '@emotion/react';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Divider } from '@spark-web/divider';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLinkButton } from '@spark-web/text-link';
import axios from 'axios';
import { FC, useState } from 'react';
import { useFetcher } from 'react-router';

import { MarketplaceVendorsQuery } from '~/gql/graphql';
import { useVendorsListUrlParams } from '~/hooks/url-params/useVendorsListUrlParams';
import { useLeadContext } from '~/provider/LeadProvider';
import { usePromoContext } from '~/provider/PromoProvider';
import { useVendorContext } from '~/provider/VendorProvider';
import { getQueryString } from '~/utils/formatter';

import { VendorImage } from '../vendors/components/vendor-image';
import PromoTag from '../vendors/promo-tag';

type VendorSelectionProps = {
  isMobile?: boolean;
};

export const VendorSelection: FC<VendorSelectionProps> = ({ isMobile = false }) => {
  const { lead } = useLeadContext();
  const {
    selectedVendors,
    setSelectedVendors,
    hasVendorsSelected,
    isAutoSelecting,
    setIsAutoSelecting,
    setRequestQuoteModal,
  } = useVendorContext();
  const { promoCode } = usePromoContext();
  const fetcher = useFetcher();

  // Get URL parameters with fallbacks for when lead doesn't exist
  const {
    leadIdParam,
    postcode,
    categories: selectedCategories,
    promotions: selectedPromotions,
    manufacturerBrands: selectedManufacturerBrands,
    channel: selectedChannel,
  } = useVendorsListUrlParams({ lead });

  // Local error state for auto-selection
  const [error, setError] = useState<string | null>(null);

  const handleRemoveVendor = (vendorPublicId: string) => {
    setSelectedVendors(selectedVendors.filter((vendor) => vendor.publicId !== vendorPublicId));
  };

  const autoSelectVendors = async () => {
    if (isAutoSelecting || hasVendorsSelected) return;

    // Clear any previous errors
    setError(null);

    // Open the modal first to show loading state
    setRequestQuoteModal(true);
    setIsAutoSelecting(true);

    const startTime = Date.now();

    try {
      // Build query string for API call
      const queryString = getQueryString([
        { name: 'leadId', value: leadIdParam },
        { name: 'postcode', value: postcode },
        { name: 'categories', value: selectedCategories?.join(',') },
        { name: 'promotions', value: selectedPromotions?.join(',') },
        { name: 'manufacturerBrands', value: selectedManufacturerBrands?.join(',') },
        { name: 'channel', value: selectedChannel },
        { name: 'sort', value: 'RANKING' },
        { name: 'take', value: '3' },
      ]);

      const { data } = await axios.get<MarketplaceVendorsQuery['marketplaceVendors']>(
        `/api/marketplace/marketplace-vendors${queryString}`,
      );

      // Ensure minimum loading time of 2 seconds for better UX
      const elapsed = Date.now() - startTime;
      if (elapsed < 2000) {
        await new Promise((resolve) => setTimeout(resolve, 2000 - elapsed));
      }

      if (data?.vendors && data.vendors.length > 0) {
        setSelectedVendors(data.vendors);
        if (lead) {
          data.vendors.forEach((vendor) => {
            fetcher.submit(
              { leadId: lead.id, vendorId: vendor.publicId },
              { method: 'post', action: '/api/marketplace/vendor-selected' },
            );
          });
        }
      }
    } catch (error) {
      // Ensure minimum loading time even on error
      const elapsed = Date.now() - startTime;
      if (elapsed < 2000) {
        await new Promise((resolve) => setTimeout(resolve, 2000 - elapsed));
      }

      setError('Failed to fetch recommended vendors. Please try again.');
      setRequestQuoteModal(false);
    } finally {
      setIsAutoSelecting(false);
    }
  };

  if (!hasVendorsSelected) {
    return (
      <Box
        borderBottom="field"
        borderTop="field"
        padding="large"
        paddingLeft={isMobile ? 'large' : 'none'}
        paddingRight={isMobile ? 'large' : 'none'}
        background="surface"
      >
        <Stack gap="medium" align="left">
          {error && (
            <Alert tone="critical" id="vendor-auto-select-error-vendor-selection">
              {error}
            </Alert>
          )}
          <Text size="small" weight="semibold" align="left">
            Not sure who to pick?
          </Text>
          <Text size="xsmall" weight="regular" align="left">
            Let us pick the top vendors in your area.
          </Text>
          <Button
            rounded
            tone="primary"
            prominence="low"
            onClick={autoSelectVendors}
            css={css({
              borderWidth: 1,
              width: '100%',
            })}
            loading={isAutoSelecting}
            disabled={isAutoSelecting}
          >
            Get recommendations
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    !isMobile && (
      <Stack gap="large" paddingY="medium">
        <Text size="small" weight="semibold">
          Vendors selected
        </Text>
        <Divider />
        {selectedVendors.map((vendor) => {
          const vendorPromo = vendor.marketplacePromotions?.find((promo) => promo.code === promoCode);

          return (
            <Stack key={vendor.publicId} gap="medium" data={{ testId: 'selected-vendors' }}>
              <Box display="flex" flexDirection="row" gap="medium">
                <VendorImage logoUrl={vendor.logoUrl} tradingName={vendor.tradingName} />
                <Stack align="left" alignSelf="center" gap="medium">
                  <Text size="small">{vendor.tradingName}</Text>
                  {vendorPromo && (
                    <PromoTag key={vendorPromo.code} theme="pantheon">
                      <Text size="xsmall">{vendorPromo.tag}</Text>
                    </PromoTag>
                  )}
                  <Text tone="info" size="xsmall" weight="regular">
                    <TextLinkButton onClick={() => handleRemoveVendor(vendor.publicId)}>Remove</TextLinkButton>
                  </Text>
                </Stack>
              </Box>
              <Divider />
            </Stack>
          );
        })}
      </Stack>
    )
  );
};
